import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Loader, Message, Panel, SelectPicker, Stack } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

const resolveApiBaseUrl = () => {
  const raw = String(API_CONFIG?.BASE_URL || '').trim();
  if (!raw) return '/api';

  const withoutTrailing = raw.replace(/\/+$/, '');
  if (/\/api$/i.test(withoutTrailing)) return withoutTrailing;
  return `${withoutTrailing}/api`;
};

const readJsonOrThrow = async (resp) => {
  const text = await resp.text();

  let payload = null;
  try {
    payload = JSON.parse(text);
  } catch {
    payload = null;
  }

  if (!resp.ok) {
    const message = payload?.error || payload?.message || `Request failed (${resp.status})`;
    throw new Error(message);
  }

  if (payload === null) {
    throw new Error('Received a non-JSON response. Check that the API base URL is correct.');
  }

  return payload;
};

/**
 * Links a ProPlan task to an hours provider's project and (optionally) a task
 * within it. Used by every provider whose linkShape is 'project-task'
 * (Clockify, EmployeeConnect).
 *
 * These providers keep projects and tasks as separate entities, so this is a
 * two-step picker rather than the single tree TimeCamp uses. Leaving the task
 * blank is a deliberate, supported choice: the link then captures every entry in
 * the project, including entries logged with no task. Task names are shown as
 * the provider returns them, so nested tasks display their full path.
 */
export default function ProviderLinkModal({
  open,
  onClose,
  providerName,
  providerDisplayName,
  taskId,
  taskName,
  onSaved,
}) {
  const providerLabel = providerDisplayName || providerName;
  const providerPath = encodeURIComponent(providerName);
  const authenticatedFetch = useAuthenticatedFetch();
  const apiBase = useMemo(() => resolveApiBaseUrl(), []);

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // The link as stored on the server, plus how many actual-hours rows already
  // exist for this task. Both drive the "this will clear old actuals" warning.
  const [savedLink, setSavedLink] = useState(null);
  const [existingActuals, setExistingActuals] = useState({ rows: 0, hours: 0, months: [] });
  const [keepExistingActuals, setKeepExistingActuals] = useState(false);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);

    try {
      const resp = await authenticatedFetch(`${apiBase}/providers/${providerPath}/projects`, { method: 'GET' });
      const rows = await readJsonOrThrow(resp);
      setProjects(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setProjects([]);
      setProjectsError(err?.message || `Failed to load ${providerLabel} projects`);
    } finally {
      setProjectsLoading(false);
    }
  }, [apiBase, authenticatedFetch, providerLabel, providerPath]);

  const loadTasks = useCallback(
    async (projectId) => {
      if (!projectId) {
        setTasks([]);
        return;
      }

      setTasksLoading(true);
      setTasksError(null);

      try {
        const url = `${apiBase}/providers/${providerPath}/projects/${encodeURIComponent(projectId)}/tasks`;
        const resp = await authenticatedFetch(url, { method: 'GET' });
        const rows = await readJsonOrThrow(resp);
        setTasks(Array.isArray(rows) ? rows : []);
      } catch (err) {
        setTasks([]);
        setTasksError(err?.message || `Failed to load ${providerLabel} tasks`);
      } finally {
        setTasksLoading(false);
      }
    },
    [apiBase, authenticatedFetch, providerLabel, providerPath]
  );

  // Seed the pickers from the link already saved on this task, so reopening the
  // dialog shows the current mapping instead of an empty form.
  const loadExistingLink = useCallback(async () => {
    if (!Number.isInteger(Number(taskId))) return;

    try {
      const url = `${apiBase}/tasks/${encodeURIComponent(String(taskId))}/provider-link?providerName=${providerPath}`;
      const resp = await authenticatedFetch(url, { method: 'GET' });
      const payload = await readJsonOrThrow(resp);

      const link = payload?.link || null;
      setSavedLink(link);
      setExistingActuals(payload?.existingActuals || { rows: 0, hours: 0, months: [] });

      if (link?.externalProjectRef) {
        setSelectedProjectId(link.externalProjectRef);
        setSelectedTaskId(link.externalTaskRef || null);
        await loadTasks(link.externalProjectRef);
      }
    } catch {
      // A missing or unreadable link is not an error worth blocking the dialog on.
    } finally {
      setInitialLoaded(true);
    }
  }, [apiBase, authenticatedFetch, loadTasks, providerPath, taskId]);

  useEffect(() => {
    if (!open) {
      setInitialLoaded(false);
      setSavedAt(null);
      setSaveError(null);
      setKeepExistingActuals(false);
      return;
    }

    loadProjects();
    loadExistingLink();
  }, [open, loadProjects, loadExistingLink]);

  const handleProjectChange = useCallback(
    (value) => {
      setSelectedProjectId(value || null);
      // A task id is only meaningful inside its own project.
      setSelectedTaskId(null);
      setSavedAt(null);
      loadTasks(value);
    },
    [loadTasks]
  );

  const handleSave = useCallback(async () => {
    if (!selectedProjectId) return;

    setSaving(true);
    setSaveError(null);

    try {
      const project = projects.find((p) => p.id === selectedProjectId) || null;
      const task = tasks.find((t) => t.id === selectedTaskId) || null;

      const url = `${apiBase}/tasks/${encodeURIComponent(String(taskId))}/provider-link`;
      const resp = await authenticatedFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerName,
          externalProjectRef: selectedProjectId,
          externalTaskRef: selectedTaskId || null,
          externalProjectName: project?.name || null,
          externalTaskName: task?.name || null,
          clearExistingActuals: !keepExistingActuals,
        }),
      });

      const payload = await readJsonOrThrow(resp);
      setSavedAt(new Date().toISOString());
      setSavedLink(payload?.link || null);
      setExistingActuals((prev) =>
        payload?.clearedActuals > 0 ? { rows: 0, hours: 0, months: [] } : prev
      );
      if (typeof onSaved === 'function') onSaved(payload);
    } catch (err) {
      setSaveError(err?.message || `Failed to save the ${providerLabel} link`);
    } finally {
      setSaving(false);
    }
  }, [apiBase, authenticatedFetch, keepExistingActuals, onSaved, projects, providerLabel, providerName, selectedProjectId, selectedTaskId, taskId, tasks]);

  const handleUnlink = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    try {
      const url = `${apiBase}/tasks/${encodeURIComponent(String(taskId))}/provider-link?providerName=${providerPath}`;
      const resp = await authenticatedFetch(url, { method: 'DELETE' });
      const payload = await readJsonOrThrow(resp);

      setSelectedProjectId(null);
      setSelectedTaskId(null);
      setTasks([]);
      setSavedAt(new Date().toISOString());
      if (typeof onSaved === 'function') onSaved(payload);
    } catch (err) {
      setSaveError(err?.message || `Failed to remove the ${providerLabel} link`);
    } finally {
      setSaving(false);
    }
  }, [apiBase, authenticatedFetch, onSaved, providerLabel, providerPath, taskId]);

  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        label: p.clientName ? `${p.name} — ${p.clientName}` : p.name,
        value: p.id,
      })),
    [projects]
  );

  const taskOptions = useMemo(() => tasks.map((t) => ({ label: t.name, value: t.id })), [tasks]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );
  const selectedTask = useMemo(() => tasks.find((t) => t.id === selectedTaskId) || null, [tasks, selectedTaskId]);

  // A link change means the stored actuals were pulled from different upstream
  // work. Only warn when there is actually something that would be discarded.
  const linkWouldChange = useMemo(() => {
    const norm = (v) => String(v ?? '').trim() || null;
    return (
      norm(savedLink?.externalProjectRef) !== norm(selectedProjectId) ||
      norm(savedLink?.externalTaskRef) !== norm(selectedTaskId)
    );
  }, [savedLink, selectedProjectId, selectedTaskId]);

  const willDiscardActuals = linkWouldChange && Number(existingActuals?.rows || 0) > 0;

  const existingActualsSummary = useMemo(() => {
    const rows = Number(existingActuals?.rows || 0);
    const hours = Number(existingActuals?.hours || 0);
    const months = Array.isArray(existingActuals?.months) ? existingActuals.months : [];
    const monthLabel = months.length === 1 ? '1 month' : `${months.length} months`;
    return `${rows} row${rows === 1 ? '' : 's'} · ${hours.toFixed(2)} hours · ${monthLabel}`;
  }, [existingActuals]);

  return (
    <Modal open={open} onClose={onClose} size="md" overflow>
      <Modal.Header>
        <Modal.Title>Link {providerLabel} project</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          Choose the <b>{providerLabel} project</b> (and optionally a task within it) that hours for{' '}
          <b>{taskName || 'this ProPlan task'}</b> are logged under.
        </div>

        <Stack justifyContent="space-between" alignItems="center" wrap style={{ marginBottom: 12 }} spacing={10}>
          <div style={{ color: '#666', fontSize: 12 }}>
            Source: <b>/api/providers/{providerName}/projects</b>
          </div>
          <Button
            appearance="subtle"
            size="xs"
            onClick={loadProjects}
            loading={projectsLoading}
            title="Refresh"
            aria-label="Refresh"
          >
            <i className="bi bi-arrow-clockwise" />
          </Button>
        </Stack>

        {projectsError ? (
          <Message type="error" showIcon style={{ marginBottom: 12 }}>
            {projectsError}
          </Message>
        ) : null}

        {tasksError ? (
          <Message type="error" showIcon style={{ marginBottom: 12 }}>
            {tasksError}
          </Message>
        ) : null}

        {saveError ? (
          <Message type="error" showIcon style={{ marginBottom: 12 }}>
            Failed to save mapping: {saveError}
          </Message>
        ) : null}

        {!initialLoaded && projectsLoading ? (
          <Stack alignItems="center" spacing={8} style={{ padding: '12px 0' }}>
            <Loader size="sm" />
            <span style={{ color: '#666', fontSize: 12 }}>Loading {providerLabel} projects…</span>
          </Stack>
        ) : null}

        <div style={{ display: 'grid', gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{providerLabel} project (required)</div>
            <SelectPicker
              block
              searchable
              cleanable
              data={projectOptions}
              value={selectedProjectId}
              loading={projectsLoading}
              placeholder={`Select the ${providerLabel} project…`}
              onChange={handleProjectChange}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              {providerLabel} task (optional — leave blank to capture the whole project)
            </div>
            <SelectPicker
              block
              searchable
              cleanable
              data={taskOptions}
              value={selectedTaskId}
              loading={tasksLoading}
              disabled={!selectedProjectId}
              placeholder={selectedProjectId ? 'All tasks in this project' : 'Pick a project first'}
              onChange={(v) => {
                setSelectedTaskId(v || null);
                setSavedAt(null);
              }}
            />
          </div>

          <Panel bordered>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>Selected mapping</div>

            {selectedProject ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, lineHeight: '18px' }}>
                    {selectedProject.name}
                    {selectedTask ? ` › ${selectedTask.name}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Project ID: {selectedProject.id}
                    {selectedTask ? ` · Task ID: ${selectedTask.id}` : ''}
                  </div>
                </div>

                {!selectedTask ? (
                  <div style={{ fontSize: 12, color: '#8c6d1f' }}>
                    All hours in this {providerLabel} project will roll up to this ProPlan task.
                  </div>
                ) : null}

                {savedAt ? <div style={{ fontSize: 12, color: '#0a6b6c', fontWeight: 700 }}>Saved to task.</div> : null}
              </div>
            ) : (
              <div style={{ color: '#666', fontSize: 12 }}>Pick a {providerLabel} project to map to this task.</div>
            )}
          </Panel>

          {willDiscardActuals ? (
            <Message type="warning" showIcon>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>
                Saving will clear this task&apos;s existing actual hours
              </div>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                This task already has stored actuals ({existingActualsSummary}). They were pulled under the previous
                link, so they no longer match what you are linking to now and will be removed. Re-pull each month to
                repopulate them from {providerLabel}.
              </div>
              <Checkbox
                checked={keepExistingActuals}
                onChange={(_v, checked) => setKeepExistingActuals(checked)}
              >
                <span style={{ fontWeight: 700 }}>Keep the existing actuals</span>
              </Checkbox>
              <div style={{ fontSize: 12, color: '#666', marginLeft: 34, marginTop: -4 }}>
                Use this if you are switching providers and will not backfill — the historical numbers stay as they
                are, and only future pulls come from {providerLabel}.
              </div>
            </Message>
          ) : null}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <NsiButton disabled={saving || !selectedProjectId} onClick={handleUnlink}>
          Unlink
        </NsiButton>
        <NsiButton onClick={onClose} disabled={saving}>
          Close
        </NsiButton>
        <NsiButton variant="primary" loading={saving} disabled={!selectedProjectId || projectsLoading} onClick={handleSave}>
          {willDiscardActuals && !keepExistingActuals ? 'Save & clear actuals' : 'Save'}
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}
